// /src/app/modules/cuentas/DetalleCuenta.tsx
import React, {useState, useEffect} from 'react'
import {useNavigate, useParams} from 'react-router-dom'
import {fetchWithAuth} from '../../core/utils/fetchWithAuth'
import {PageTitle} from '../../../_metronic/layout/core'
import {CuentaInfoCard, ConceptosCuentaModal, MovimientosSection} from './components'
import '../socios/socios.scss'

type Concepto = {
  id: number
  nombre: string
  valor: number
  descripcion: string
  recurrente: boolean
  variable: boolean
}

type Movimiento = {
  id: string
  nroFactura: string
  idPago: string
  fecha: string
  monto: string
  tipo: string
  estado: string
}

type Resumen = {
  totalEgresos: number
  totalIngresos: number
  balance: number
}

type CuentaDetalle = {
  numero: number
  activa: number
  idMiembro: number
  categoria: number
  zonaCobro: number | null
  exentaIva: number
  exentaIrae: number
  categoriaNombre: string
  nombreFantasia?: string
  razonSocial?: string
  socio?: number
  movimientos: Movimiento[]
  resumen: Resumen
  conceptos: Concepto[]
}

function DetalleCuentaPage() {
  const navigate = useNavigate()
  const {numero} = useParams<{numero: string}>()
  
  const [cuenta, setCuenta] = useState<CuentaDetalle | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [conceptosModalOpen, setConceptosModalOpen] = useState<boolean>(false)
  const [savingConceptos, setSavingConceptos] = useState<boolean>(false)

  const getToken = (): string | null => localStorage.getItem('X-TOKEN')

  const handleAuthResponse = (response: Response, data: any): boolean => {
    if (response.status === 401 || 
        (data.code === 'ERROR' && data.msg === 'No Autorizado') ||
        (data.code === 'ERROR' && data.returned_message?.includes('JWT expired'))) {
      localStorage.removeItem('X-TOKEN')
      navigate('/auth/login')
      return true
    }
    return false
  }

  const fetchCuentaDetalle = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const token = getToken()
      if (!token) {
        throw new Error('No se encontró el token de autenticación')
      }

      const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-TOKEN': token
        },
        body: JSON.stringify({
          service: 'getCuenta',
          numero: numero
        })
      })

      if (!response.ok) {
        throw new Error(`Error en la petición: ${response.status}`)
      }

      const data = await response.json()
      
      if (handleAuthResponse(response, data)) {
        return
      }
      
      if (data.code === 'OK') {
        setCuenta(data.data)
      } else {
        throw new Error(data.returned_message || 'Error en la respuesta del servidor')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (numero) {
      fetchCuentaDetalle()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numero])

  const handleEditarConceptos = () => {
    setConceptosModalOpen(true)
  }

  const handleActualizarConceptos = async (conceptosActualizados: Concepto[]) => {
    if (!cuenta) return

    try {
      setSavingConceptos(true)
      setError(null)
      const token = getToken()
      if (!token) throw new Error('No se encontró el token de autenticación')

      const conceptosPayload = conceptosActualizados.map((c) => ({
        idConcepto: c.id,
        valor: parseFloat(String(c.valor)) || 0,
      }))

      const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-TOKEN': token,
        },
        body: JSON.stringify({
          service: 'updateCuenta',
          numero: cuenta.numero,
          activa: cuenta.activa,
          categoria: cuenta.categoria,
          zonaCobro: cuenta.zonaCobro ?? 0,
          exentaIva: cuenta.exentaIva,
          exentaIrae: cuenta.exentaIrae,
          conceptos: conceptosPayload,
        }),
      })

      if (!response.ok) throw new Error(`Error en la petición: ${response.status}`)
      const data = await response.json()

      if (handleAuthResponse(response, data)) return

      if (data.code === 'OK') {
        setConceptosModalOpen(false)
        await fetchCuentaDetalle()
      } else {
        throw new Error(data.returned_message || 'Error al actualizar la cuenta')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido')
    } finally {
      setSavingConceptos(false)
    }
  }

  if (loading) {
    return (
      <>
        <PageTitle>Cargando...</PageTitle>
        <div className='card'>
          <div className='card-body'>
            <div className='d-flex justify-content-center align-items-center' style={{minHeight: '400px'}}>
              <div className='d-flex flex-column align-items-center'>
                <div className='spinner-border text-primary' role='status' style={{width: '3rem', height: '3rem'}}>
                  <span className='visually-hidden'>Cargando...</span>
                </div>
                <p className='mt-4 text-muted'>Cargando información de la cuenta...</p>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }

  if (error || !cuenta) {
    return (
      <>
        <PageTitle>Error</PageTitle>
        <div className='card'>
          <div className='card-body'>
            <div className='alert alert-danger d-flex align-items-center p-5'>
              <i className='ki-duotone ki-information-5 fs-2hx text-danger me-4'>
                <span className='path1'></span>
                <span className='path2'></span>
                <span className='path3'></span>
              </i>
              <div className='d-flex flex-column'>
                <h4 className='mb-1 text-danger'>Error al cargar la cuenta</h4>
                <span>{error || 'No se encontró la cuenta'}</span>
              </div>
              <button 
                className='btn btn-light-primary ms-auto'
                onClick={() => navigate('/cuentas/listado')}
              >
                Volver al listado
              </button>
            </div>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <PageTitle>Detalle de Cuenta #{cuenta.numero}</PageTitle>
      
      {/* Breadcrumb y acciones */}
      <div className='d-flex justify-content-between align-items-center mb-6'>
        <nav aria-label='breadcrumb'>
          <ol className='breadcrumb'>
            <li className='breadcrumb-item'>
              <button 
                className='btn btn-link p-0 text-decoration-none'
                onClick={() => navigate('/cuentas/listado')}
              >
                Cuentas
              </button>
            </li>
            <li className='breadcrumb-item active' aria-current='page'>
              Cuenta #{cuenta.numero}
            </li>
          </ol>
        </nav>
        <div className='d-flex gap-2'>
          <button 
            className='btn btn-light-primary'
            onClick={() => navigate('/cuentas/listado')}
          >
            <i className='ki-duotone ki-arrow-left fs-3 me-1'>
              <span className='path1'></span>
              <span className='path2'></span>
            </i>
            Volver
          </button>
          {cuenta.idMiembro && (
            <button
              className='btn btn-light-info'
              onClick={() => navigate(`/socios/${cuenta.idMiembro}`)}
            >
              <i className='ki-duotone ki-profile-user fs-3 me-1'>
                <span className='path1'></span>
                <span className='path2'></span>
                <span className='path3'></span>
                <span className='path4'></span>
              </i>
              Ver Socio
            </button>
          )}
        </div>
      </div>

      <div className='row g-6 g-xl-9'>
        {/* Información general de la cuenta */}
        <div className='col-12'>
          <CuentaInfoCard 
            cuenta={cuenta}
            onEditar={() => {}}
          />
        </div>

        {/* Sección de conceptos */}
        <div className='col-lg-6'>
          <div className='card h-100'>
            <div className='card-header border-0 pt-6'>
              <div className='card-title'>
                <i className='ki-duotone ki-tag fs-2 me-2 text-primary'>
                  <span className='path1'></span>
                  <span className='path2'></span>
                  <span className='path3'></span>
                </i>
                <h3 className='fw-bold m-0'>Conceptos de Facturación</h3>
              </div>
              <div className='card-toolbar'>
                <button 
                  className='btn btn-sm btn-primary'
                  onClick={handleEditarConceptos}
                >
                  <i className='ki-duotone ki-pencil fs-4 me-1'>
                    <span className='path1'></span>
                    <span className='path2'></span>
                  </i>
                  Editar Conceptos
                </button>
              </div>
            </div>
            <div className='card-body pt-0'>
              {cuenta.conceptos.length > 0 ? (
                <div className='table-responsive'>
                  <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                    <thead>
                      <tr className='fw-bold text-muted bg-light'>
                        <th>Concepto</th>
                        <th className='text-end'>Valor</th>
                        <th className='text-center'>Tipo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cuenta.conceptos.map((concepto) => (
                        <tr key={concepto.id}>
                          <td>
                            <div className='d-flex flex-column'>
                              <span className='fw-bold text-gray-800'>{concepto.nombre}</span>
                              {concepto.descripcion && (
                                <span className='text-muted fs-7'>{concepto.descripcion}</span>
                              )}
                            </div>
                          </td>
                          <td className='text-end fw-bold'>${concepto.valor.toFixed(2)}</td>
                          <td className='text-center'>
                            {concepto.recurrente && (
                              <span className='badge badge-light-primary me-1'>Recurrente</span>
                            )}
                            {concepto.variable && (
                              <span className='badge badge-light-warning'>Variable</span>
                            )}
                          </td>
                        </tr>
                      ))}
                      <tr className='bg-light-primary'>
                        <td className='fw-bold text-primary'>TOTAL</td>
                        <td className='text-end fw-bold text-primary fs-4'>
                          ${cuenta.conceptos.reduce((acc, c) => acc + c.valor, 0).toFixed(2)}
                        </td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className='d-flex flex-column align-items-center py-10'>
                  <i className='ki-duotone ki-information-5 fs-3x text-muted mb-3'>
                    <span className='path1'></span>
                    <span className='path2'></span>
                    <span className='path3'></span>
                  </i>
                  <p className='text-muted'>No hay conceptos configurados para esta cuenta</p>
                  <button 
                    className='btn btn-sm btn-primary mt-2'
                    onClick={handleEditarConceptos}
                  >
                    Configurar Conceptos
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Resumen financiero */}
        <div className='col-lg-6'>
          <div className='card h-100'>
            <div className='card-header border-0 pt-6'>
              <div className='card-title'>
                <i className='ki-duotone ki-chart-simple-2 fs-2 me-2 text-success'>
                  <span className='path1'></span>
                  <span className='path2'></span>
                  <span className='path3'></span>
                  <span className='path4'></span>
                </i>
                <h3 className='fw-bold m-0'>Resumen Financiero</h3>
              </div>
            </div>
            <div className='card-body pt-0'>
              <div className='d-flex flex-column gap-6'>
                <div className='d-flex align-items-center p-4 bg-light-success rounded'>
                  <div className='symbol symbol-50px me-4'>
                    <div className='symbol-label bg-success'>
                      <i className='ki-duotone ki-arrow-up fs-2x text-white'>
                        <span className='path1'></span>
                        <span className='path2'></span>
                      </i>
                    </div>
                  </div>
                  <div className='flex-grow-1'>
                    <span className='text-muted fw-semibold d-block fs-7'>Total Ingresos</span>
                    <span className='text-gray-800 fw-bold fs-2'>${cuenta.resumen.totalIngresos.toFixed(2)}</span>
                  </div>
                </div>

                <div className='d-flex align-items-center p-4 bg-light-danger rounded'>
                  <div className='symbol symbol-50px me-4'>
                    <div className='symbol-label bg-danger'>
                      <i className='ki-duotone ki-arrow-down fs-2x text-white'>
                        <span className='path1'></span>
                        <span className='path2'></span>
                      </i>
                    </div>
                  </div>
                  <div className='flex-grow-1'>
                    <span className='text-muted fw-semibold d-block fs-7'>Total Egresos</span>
                    <span className='text-gray-800 fw-bold fs-2'>${cuenta.resumen.totalEgresos.toFixed(2)}</span>
                  </div>
                </div>

                <div className={`d-flex align-items-center p-4 ${cuenta.resumen.balance >= 0 ? 'bg-light-primary' : 'bg-light-warning'} rounded`}>
                  <div className='symbol symbol-50px me-4'>
                    <div className={`symbol-label ${cuenta.resumen.balance >= 0 ? 'bg-primary' : 'bg-warning'}`}>
                      <i className='ki-duotone ki-chart-line-up fs-2x text-white'>
                        <span className='path1'></span>
                        <span className='path2'></span>
                      </i>
                    </div>
                  </div>
                  <div className='flex-grow-1'>
                    <span className='text-muted fw-semibold d-block fs-7'>Balance</span>
                    <span className='text-gray-800 fw-bold fs-1'>${cuenta.resumen.balance.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sección de movimientos */}
        <div className='col-12'>
          <MovimientosSection numeroCuenta={cuenta.numero} />
        </div>
      </div>

      {/* Modal de conceptos */}
      <ConceptosCuentaModal
        isOpen={conceptosModalOpen}
        onClose={() => setConceptosModalOpen(false)}
        cuenta={cuenta}
        onApply={handleActualizarConceptos}
        isSaving={savingConceptos}
      />
    </>
  )
}

export default DetalleCuentaPage

